/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import CardComponent from 'pages/Front/Booking/components/CardComponent'
import { PercentBadgeIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useAppDispatch } from 'helpers/store'
import { useTranslation } from 'react-i18next'
import { PackagesCart } from 'types/entities/PackagesCart'
import { currencyNumberFormat } from 'utils/utility'
import { usePackagesCart } from 'utils/packageUtils'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'utils/toastUtils'
import { closeSecondaryModal } from 'helpers/actions'
import SellerAction from 'store/seller/SellerAction'
import {
    useBookingSellerVoucherSchema,
    BookingSellerVoucherInputs,
} from 'pages/Front/Booking/Seller/schemas/useBookingSellerVoucherSchema'
import UiButton, { UiButtonStyle } from 'components/Ui/UiButton'
import UiInput from 'components/Ui/UiInput'

interface BookingSellerVoucherModalProps {
    packagesCart: PackagesCart
    closeModal?: () => void
}

const BookingSellerNewVoucherModal = (
    props: BookingSellerVoucherModalProps
) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { priceByType } = usePackagesCart(props.packagesCart)

    // const [upsellPriceDiscount, setUpsellPriceDiscount] = React.useState<number>(priceDiscount / 100 || 0);
    const [voucherPriceDiscount, setVoucherPriceDiscount] =
        React.useState<number>(0)
    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    const BookingSellerVoucherSchema = useBookingSellerVoucherSchema()
    const { register, setValue, watch } = useForm<BookingSellerVoucherInputs>({
        resolver: zodResolver(BookingSellerVoucherSchema),
        defaultValues: {
            priceDiscount: 0,
        },
    })

    const priceDiscount = watch('priceDiscount')

    const addDiscountHandler = () => {
        if (isLoading) {
            return
        }

        setIsLoading(true)
        dispatch(
            SellerAction.fetchSellerAddVoucher(
                props.packagesCart.id,
                'price',
                voucherPriceDiscount * 100
            )
        )
            .then((r) => {
                if (props.closeModal) {
                    props.closeModal()
                } else {
                    dispatch(closeSecondaryModal())
                }
                toast.success(t('Upsell changed'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const onChangePriceDiscountHandler = (newValue: number) => {
        setVoucherPriceDiscount(+newValue)
    }

    useEffect(() => {
        onChangePriceDiscountHandler(+priceDiscount)
    }, [priceDiscount])

    const inputClass =
        'flex rounded-md border leading-tight w-full py-1 px-2 h-[30px]'

    return (
        <CardComponent
            className={clsx(
                'BookingSellerNewVoucherModal w-full !p-0 border border-gray-200'
            )}
        >
            <div
                className={clsx(
                    'flex flex-row justify-between w-full px-3 py-3 font-bold border-b-2 border-gray-200 dark:border-gray-800 bg-gray-100/20 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 drop-shadow-sm'
                )}
            >
                <div className={clsx('flex flex-row items-center')}>
                    <PercentBadgeIcon
                        className={clsx(
                            'w-5 h-5 mr-1 text-gray-600/70 dark:text-gray-400 drop-shadow-xl'
                        )}
                    />
                    <h4
                        className={clsx(
                            'font-medium text-gray-700 dark:text-gray-300'
                        )}
                    >
                        {t('Discount')}
                    </h4>
                </div>
            </div>

            <div
                className={clsx(
                    'flex w-full flex-col px-3 pt-3 pb-4 bg-white dark:bg-gray-900 overflow-hidden h-auto rounded-bl-lg rounded-br-lg'
                )}
            >
                <div className={clsx('flex flex-col overflow-y-auto h-full')}>
                    <h4
                        className={clsx(
                            'font-bold text-lg text-gray-900 dark:text-gray-100'
                        )}
                    >
                        {t('Item information')}
                    </h4>
                    <div className={clsx('mb-6')}>
                        <table className={clsx('w-full')}>
                            <tbody>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Total price of items'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            priceByType(
                                                PackageItemItemType.PackageItem
                                            )
                                        )}
                                    </td>
                                </tr>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Total price of upsells'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            priceByType(
                                                PackageItemItemType.Upsell
                                            )
                                        )}
                                    </td>
                                </tr>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Current discount'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            voucherPriceDiscount * 100
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        className={clsx(
                            'Actions grid grid-cols-3 gap-x-2 gap-y-3 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-3 shadow bg-gray-50 dark:bg-gray-800'
                        )}
                    >
                        <div className={clsx('cols-span-1')}>
                            <UiInput
                                label={t('CHF discount')}
                                {...register('priceDiscount')}
                                type="number"
                                min={0}
                                max={1000}
                                step={1}
                                placeholder={currencyNumberFormat(
                                    priceDiscount
                                )}
                                disabled={isLoading}
                            />
                        </div>

                        <div className={clsx('col-span-3 text-center')}>
                            <UiButton
                                className="mx-auto"
                                style={UiButtonStyle.Green}
                                onClick={addDiscountHandler}
                                disabled={isLoading}
                                icon={
                                    <CheckIcon
                                        className={clsx('w-5 h-5 flex-none')}
                                    />
                                }
                            >
                                {isLoading
                                    ? t('Processing...')
                                    : t('Apply discount')}
                            </UiButton>
                        </div>
                    </div>
                </div>
            </div>
        </CardComponent>
    )
}

export default BookingSellerNewVoucherModal
